import { ConfirmDialog, Dialog } from "components/dialog";
import { UnitsEntryType } from "constants/product-collection";
import { IAllocationDimension } from "helpers/allocation-dimension";
import { IPurchaseOrderDoorGroup } from "helpers/delivery-purchase-order";
import { ISchemaKey } from "helpers/product-collection";
import { WindowAnalytics } from "helpers/window-analytics";
import { formatMessage } from "lib/globalize-util";
import * as React from "react";
import { FeatureFlags } from "helpers/feature-flags";
import { useFlags } from "launchdarkly-react-client-sdk";
import { AddOrEditAttributesView } from "./add-or-edit-attributes-view";
import { useUploader } from "./import-door-attributes/context-api/hooks/use-uploader";
import { UploaderFileToImportAttributes } from "./import-door-attributes/uploader-file-to-import-door";
import { styles } from "./manage-allocation-dimension-styles";
import { ManageAllocationDimensionTitle } from "./manage-allocation-dimension-title";
import { TabValues } from "./constants";
import { TabPanel } from "../product-collection-views/product-collection-grid-view/tool-panels/dimensions-tool-panel/tabs/tab-panel";
import CreateDoorAttributesDialogContainer from "../product-collection-views/product-collection-grid-view/tool-panels/dimensions-tool-panel/tabs/attributes/create-door-attributes-dialog-container";
import { ManageAllocationDimensionTabs } from "./manage-allocation-dimension-tabs";

interface IProps {
    doorsDimension: IAllocationDimension;
    onToggleShow: () => void;
    onMutateAllocationDimensionValue: (allocationDimensionId, item) => void;
    onMoveAllocationDimensionValue: (allocationDimensionId: string, index: number, destinationIndex: number) => void;
    isSaving?: boolean;
    readOnly?: boolean;
    merchantPurchaseOrderDoorGroups: IPurchaseOrderDoorGroup[];
    unitsEntryType?: UnitsEntryType;
    onPurchaseOrdersError?: () => void;
    isRollupMode: boolean;
    doorClassificationSchema: ISchemaKey[];
    onCustomDoorCellValueChanged: (
        attributes: ISchemaKey[],
        allocationDimensionId: string,
        rowIndexToDataMap: any
    ) => void;
    updateAllocationDimensionAttribute: (
        allocationDimensionAttribute: ISchemaKey[],
        allocationDimensionId: string
    ) => void;
    collectionsRefs: Map<string, string>;
    assortmentName?: string;
    manageAllocationDimensionTab?: TabValues;
    isOpenManageAllocationDimension: boolean;
}
export const ManageAllocationDimension: React.FunctionComponent<IProps> = ({
    doorsDimension,
    onToggleShow,
    onMutateAllocationDimensionValue,
    onMoveAllocationDimensionValue,
    isSaving,
    readOnly,
    merchantPurchaseOrderDoorGroups,
    unitsEntryType,
    onPurchaseOrdersError,
    isRollupMode,
    onCustomDoorCellValueChanged,
    updateAllocationDimensionAttribute,
    collectionsRefs,
    manageAllocationDimensionTab,
    assortmentName,
    isOpenManageAllocationDimension,
}) => {
    const [editMode, setEditDoorAttribute] = React.useState<string | undefined>(undefined);
    const [deleteMode, setDeleteMode] = React.useState<string | undefined>(undefined);
    const [tabValue, setTabValue] = React.useState<TabValues>(
        manageAllocationDimensionTab ?? TabValues.AddOrEditAttributes
    );
    const [disableEscapeKeyDown, setDisableEscapeKeyDown] = React.useState<boolean>(false);
    const flags: FeatureFlags = useFlags();

    const {
        handleDrop,
        errorMessages,
        isUpdating,
        isUploading,
        wasProcessed,
        hasDataToChange,
        handleOpenConfirmDialog,
        clearValueStates,
        handleClearErrors,
    } = useUploader();

    React.useEffect(() => {
        if (manageAllocationDimensionTab) {
            setTabValue(manageAllocationDimensionTab);
        }
    }, [manageAllocationDimensionTab]);

    const handleToggleShow = React.useCallback(() => {
        if (!disableEscapeKeyDown) {
            clearValueStates();
            onToggleShow();
        } else {
            setDisableEscapeKeyDown(false);
        }
    }, [clearValueStates, disableEscapeKeyDown, onToggleShow]);

    const handleCloseDialog = React.useCallback(() => {
        setDeleteMode(undefined);
    }, [setDeleteMode]);

    const handleCloseEditAttributeDialog = React.useCallback(() => {
        clearValueStates();
        // Reset tab's value before to close the Dialog
        setTabValue(TabValues.AddOrEditAttributes);
        onToggleShow();
    }, [clearValueStates, onToggleShow]);

    const handleDeleteDoorGrouping = React.useCallback(() => {
        let newAllocationDimensionAttributes = [];
        let allocationDimensionAttributesCopy = [...doorsDimension.attributes.keys];
        allocationDimensionAttributesCopy = allocationDimensionAttributesCopy.filter(
            (attribute) => attribute.field_name !== deleteMode
        );
        newAllocationDimensionAttributes = [...allocationDimensionAttributesCopy];

        let rowIndexToDataMap;
        doorsDimension.dimension_values.forEach((row, index) => {
            if (row.fields?.[deleteMode]) {
                if (!rowIndexToDataMap) {
                    rowIndexToDataMap = new Map<number, any>();
                }
                const rowToUpdate = { ...row };
                delete rowToUpdate.fields[deleteMode];
                rowIndexToDataMap.set(index, rowToUpdate);
            }
        });
        if (rowIndexToDataMap) {
            onCustomDoorCellValueChanged(newAllocationDimensionAttributes, doorsDimension.id, rowIndexToDataMap);
        } else {
            updateAllocationDimensionAttribute(newAllocationDimensionAttributes, doorsDimension.id);
        }
        setDeleteMode(undefined);
        WindowAnalytics.track("Delete custom attribute", {});
    }, [doorsDimension, deleteMode, updateAllocationDimensionAttribute, onCustomDoorCellValueChanged]);

    const handlePressNextButtonOnUploadDoorAttributes = () => {
        handleOpenConfirmDialog?.();
    };
    const isImportDoorAttributesEnabled = flags.importDoorAttributes && !readOnly && !isRollupMode;
    const handleChangeTab = (index) => {
        setTabValue(index);

        if (index !== TabValues.ImportAttributes && isImportDoorAttributesEnabled) {
            clearValueStates?.();
        }
    };

    if (doorsDimension) {
        if (editMode) {
            return (
                <CreateDoorAttributesDialogContainer
                    isDialogOpen={!!editMode}
                    onCancel={() => setEditDoorAttribute(undefined)}
                    attributeKey={editMode}
                />
            );
        }
        if (deleteMode) {
            return (
                <ConfirmDialog
                    title=""
                    open={!!deleteMode}
                    onConfirm={handleDeleteDoorGrouping}
                    onCancel={handleCloseDialog}
                >
                    <p>{formatMessage("Are you sure you want to delete this door group?")}</p>
                </ConfirmDialog>
            );
        }
        return (
            <Dialog
                modal={false}
                open={isOpenManageAllocationDimension}
                contentStyle={styles.modalContainer}
                bodyStyle={styles.bodyContainer}
                contentClassName={`${styles.content}`}
                onRequestClose={handleToggleShow}
                showCloseButton={false}
                autoDetectWindowHeight={false}
                autoScrollBodyContent={false}
                disableEscapeKeyDown={disableEscapeKeyDown}
                title={
                    <ManageAllocationDimensionTitle
                        allocationDimension={doorsDimension}
                        isRollupMode={isRollupMode}
                        onToggleShow={handleCloseEditAttributeDialog}
                        isSaving={isSaving}
                    />
                }
            >
                <div {...styles.tabs}>
                    <ManageAllocationDimensionTabs
                        onChangeTab={handleChangeTab}
                        tabValue={tabValue}
                        isImportDoorAttributesEnabled={isImportDoorAttributesEnabled}
                    />
                    <TabPanel value={tabValue} index={TabValues.AddOrEditAttributes}>
                        <AddOrEditAttributesView
                            allocationDimension={doorsDimension}
                            onMutateAllocationDimensionValue={onMutateAllocationDimensionValue}
                            onMoveAllocationDimensionValue={onMoveAllocationDimensionValue}
                            readOnly={readOnly}
                            merchantPurchaseOrderDoorGroups={merchantPurchaseOrderDoorGroups}
                            unitsEntryType={unitsEntryType}
                            onPurchaseOrdersError={onPurchaseOrdersError}
                            isRollupMode={isRollupMode}
                            onCustomDoorCellValueChanged={onCustomDoorCellValueChanged}
                            collectionsRefs={collectionsRefs}
                            setEditDoorAttribute={setEditDoorAttribute}
                            setDeleteMode={setDeleteMode}
                            setDisableEscapeKeyDown={setDisableEscapeKeyDown}
                            assortmentName={assortmentName}
                        />
                    </TabPanel>
                    <TabPanel value={tabValue} index={TabValues.ImportAttributes}>
                        <UploaderFileToImportAttributes
                            errorMessages={errorMessages}
                            handleDrop={handleDrop}
                            hasDataToChange={hasDataToChange}
                            isUpdating={isUpdating}
                            isUploading={isUploading}
                            wasProcessed={wasProcessed}
                            handlePressNextButton={handlePressNextButtonOnUploadDoorAttributes}
                            clearValueStates={clearValueStates}
                            handleClearErrors={handleClearErrors}
                        />
                    </TabPanel>
                </div>
            </Dialog>
        );
    }

    return null;
};
